import React, { useCallback, useMemo } from "react";
import Avatar from "../../theme/untitled/Avatar/Avatar";
import Badge from "../../theme/untitled/Badge/Badge";
import Icon from "../../theme/untitled/icons/Icon";
import RowActionsMenu from "../../theme/untitled/RowActionsMenu/RowActionsMenu";
import CopyEmailButton from "./CopyEmailButton";
import { AppUserRole } from "../../consts/users";
import { dateTimeFormatter } from "../../helpers/admin";
import styles from "./UsersPage.module.scss";

const formatTimestamp = (seconds) =>
  seconds ? (
    dateTimeFormatter.format(new Date(seconds * 1000))
  ) : (
    <span className={styles.dash}>&mdash;</span>
  );

const initials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "?";
};

/**
 * @param {boolean} disabled
 * @param {function} onAutoLogin
 * @param {function} onDelete
 * @param {function} onShowEditModal
 * @param {Object} user
 * @param {string} user.uuid
 * @param {string} user.name
 * @param {string} user.email
 * @param {string} user.role
 * @param {number} user.created
 * @param {number=} user.last_login - unix seconds, falsy if never logged in
 * @param {boolean} user.is_account_verified
 * @param {boolean} user.is_google_account
 * @param {function} onRefreshIntercomTags
 */
const TableRow = ({
  disabled,
  onAutoLogin,
  onDelete,
  onShowEditModal,
  user,
  onRefreshIntercomTags,
}) => {
  // DropdownMenu items are plain <button type="button"> invoked with no
  // event (see theme/untitled/DropdownMenu's `item.onClick?.()`), unlike the
  // old <a href="#"> HamburgerItem this replaced — no preventDefault needed.
  const onUpdateUser = useCallback((callback) => () => callback(user), [user]);

  const {
    name,
    email,
    created,
    last_login,
    role,
    is_account_verified,
    is_google_account,
  } = user;
  const isVerified = is_account_verified * 1 === 1;

  const adminPill = role === AppUserRole.admin && (
    <span className={styles.adminPill}>
      <Icon name="Shield01" size={12} />
      Admin
    </span>
  );

  const googleSignUpMeta = is_google_account && (
    <span className={styles.metaLine}>
      <img
        src="/img/google_account.png"
        alt="Account created with Google Signup"
        className={styles.googleIcon}
        width={14}
        height={14}
      />
      Google sign-up
    </span>
  );

  const verifiedStatus = isVerified ? (
    <span className={styles.verifiedText}>
      <Icon name="CheckCircle" size={16} className={styles.verifiedIcon} />
      Verified
    </span>
  ) : (
    <Badge color="warning" size="sm" dot>
      Unverified
    </Badge>
  );

  const menuOptions = useMemo(
    () => [
      {
        key: "details",
        label: "Details",
        icon: <Icon name="User01" size={20} />,
        onClick: onUpdateUser(onShowEditModal),
      },
      {
        key: "autologin",
        label: "Auto login",
        icon: <Icon name="Key01" size={20} />,
        onClick: onUpdateUser(onAutoLogin),
      },
      {
        key: "intercom",
        label: "View in Intercom",
        icon: <Icon name="ArrowUpRight" size={20} />,
        onClick: () =>
          window.open(
            `https://app.intercom.com/a/apps/${process.env.INTERCOM_APP_ID}/users/${user.intercom_hash}`,
            "_blank"
          ),
      },
      {
        key: "refresh-tags",
        label: "Refresh Intercom tags",
        icon: <Icon name="RefreshCcw01" size={20} />,
        onClick: onUpdateUser(onRefreshIntercomTags),
      },
      { key: "divider", type: "divider" },
      {
        key: "delete",
        label: "Delete",
        icon: <Icon name="Trash01" size={20} />,
        destructive: true,
        onClick: onUpdateUser(onDelete),
      },
    ],
    [
      onUpdateUser,
      onShowEditModal,
      onAutoLogin,
      onRefreshIntercomTags,
      onDelete,
      user,
    ]
  );

  return (
    <tr className={styles.row}>
      <td>
        <div className={styles.nameCell}>
          <Avatar className={styles.avatar} initials={initials(name)} />
          <div className={styles.nameBlock}>
            <div className={styles.nameLine}>
              {name}
              {adminPill}
            </div>
            {googleSignUpMeta}
          </div>
        </div>
      </td>
      <td>
        <div className={styles.emailCell}>
          <span className={styles.emailText}>{email}</span>
          <CopyEmailButton value={email} />
        </div>
      </td>
      <td>{verifiedStatus}</td>
      <td className={styles.joinCell}>{formatTimestamp(created)}</td>
      <td className={styles.lastLoginCell}>{formatTimestamp(last_login)}</td>
      <td>
        <RowActionsMenu items={menuOptions} disabled={disabled} />
      </td>
    </tr>
  );
};

export default TableRow;
